import { nullSafeToString } from '../lib/util';

/*
 * Mirrors SiteSettingsDTO, the read-only storefront-safe view of a site's configuration. The API
 * groups these values (branding, contact, urls, features, commerce, access, loyalty, system) and that
 * grouping is preserved here because it is what makes the surface self-describing.
 *
 * Note there is no `isLive` flag: nothing in this payload distinguishes a live site from a test one.
 * `system.showTestingWatermark` is a related but separate setting and should not be used as a proxy.
 */

/**
 * Read-only, storefront-safe settings for a site.
 *
 * @property {number} id - (Read only) The site id.
 * @property {Collaterate.Site~settingsBranding} branding - (Read only) Branding and theme identity values.
 * @property {Collaterate.Site~settingsContact} contact - (Read only) Public contact information.
 * @property {Collaterate.Site~settingsUrls} urls - (Read only) Site URLs.
 * @property {Collaterate.Site~settingsFeatures} features - (Read only) Feature toggles that affect the UI.
 * @property {Collaterate.Site~settingsCommerce} commerce - (Read only) Commerce and checkout display settings.
 * @property {Collaterate.Site~settingsAccess} access - (Read only) Access related settings.
 * @property {Collaterate.Site~settingsLoyalty} loyalty - (Read only) Public loyalty settings.
 * @property {Collaterate.Site~settingsSystem} system - (Read only) Global settings relevant to storefront UI.
 */

class SiteSettings {
    constructor (data = {}) {
        const {
            branding = {},
            contact = {},
            urls = {},
            features = {},
            commerce = {},
            access = {},
            loyalty = {},
            system = {},
        } = data;

        this.id = data.id;

        this.branding = Object.freeze({
            name: nullSafeToString(branding.name),
            shortName: nullSafeToString(branding.shortName),
            logoPath: nullSafeToString(branding.logoPath),
            secondaryLogoPath: nullSafeToString(branding.secondaryLogoPath),
            faviconPath: nullSafeToString(branding.faviconPath),
            indicatorColor: nullSafeToString(branding.indicatorColor),
            usesLegacyTheme: Boolean(branding.useLegacyTheme),
        });

        this.contact = Object.freeze({
            email: nullSafeToString(contact.email),
            phone: nullSafeToString(contact.phone),
            companyName: nullSafeToString(contact.companyName),
            companyUrl: nullSafeToString(contact.companyUrl),
            city: nullSafeToString(contact.city),
            stateId: contact.stateId,
            stateName: nullSafeToString(contact.stateName),
            countryId: contact.countryId,
            countryName: nullSafeToString(contact.countryName),
        });

        this.urls = Object.freeze({
            domainName: nullSafeToString(urls.domainName),
            subdomainPrefix: nullSafeToString(urls.subdomainPrefix),
            homeUrl: nullSafeToString(urls.homeUrl),
        });

        this.features = Object.freeze({
            loyaltyEnabled: Boolean(features.loyaltyEnabled),
            dataProfilesEnabled: Boolean(features.dataProfilesEnabled),
            designToolEnabled: Boolean(features.designToolEnabled),
            multipleProjectsEnabled: Boolean(features.multipleProjectsEnabled),
            backOrderEnabled: Boolean(features.backOrderEnabled),
            subscriptionDigitalDownloadEnabled: Boolean(features.subscriptionDigitalDownloadEnabled),
            storefrontFlowV2: Boolean(features.storefrontFlowV2),
            divisionOrderingEnabled: Boolean(features.divisionOrderingEnabled),
        });

        this.commerce = Object.freeze({
            showPricing: Boolean(commerce.showPricing),
            showDueDate: Boolean(commerce.showDueDate),
            isTaxExempt: Boolean(commerce.taxExempt),
            orderApprovalEnabled: Boolean(commerce.orderApprovalEnabled),
            allowStockQuantityChangeFromCart: Boolean(commerce.allowStockQuantityChangeFromCart),
            allowPodQuantityChangeFromCart: Boolean(commerce.allowPodQuantityChangeFromCart),
            allowCheckoutPaymentTypeOverride: Boolean(commerce.allowCheckoutPaymentTypeOverride),
            allowUserAssignedCustomQuote: Boolean(commerce.allowUserAssignedCustomQuote),
            allowAnonymousCustomQuote: Boolean(commerce.allowAnonymousCustomQuote),
            addToCartRedirectsToHome: Boolean(commerce.addToCartRedirectsToHome),
            defaultShipCountryId: commerce.defaultShipCountryId,
            defaultShipCountryName: nullSafeToString(commerce.defaultShipCountryName),
        });

        this.access = Object.freeze({
            isPrivateSite: Boolean(access.privateSite),
            sessionTimeoutMinutes: access.sessionTimeoutMinutes,
        });

        this.loyalty = Object.freeze({
            isEnabled: Boolean(loyalty.enabled),
            publicApiKey: nullSafeToString(loyalty.publicApiKey),
            pointsLabel: nullSafeToString(loyalty.pointsLabel),
        });

        this.system = Object.freeze({
            uploadMaxSizeBytes: system.uploadMaxSizeBytes,
            cacheBusting: nullSafeToString(system.cacheBusting),
            clientDebug: Boolean(system.clientDebug),
            showTestingWatermark: Boolean(system.showTestingWatermark),
        });

        Object.freeze(this);
    }
}

export default SiteSettings;

/**
 * @typedef {Object} Collaterate.Site~settingsBranding
 * @property {string} name - Display name of the site.
 * @property {string} shortName - Short name of the site.
 * @property {string} logoPath - Primary logo image path.
 * @property {string} secondaryLogoPath - Secondary logo image path.
 * @property {string} faviconPath - Favicon image path.
 * @property {string} indicatorColor - Site accent color.
 * @property {boolean} usesLegacyTheme - Whether the site uses the legacy theme.
 */

/**
 * @typedef {Object} Collaterate.Site~settingsContact
 * @property {string} email - Public site contact email.
 * @property {string} phone - Public company phone.
 * @property {string} companyName - Company name.
 * @property {string} companyUrl - Company website URL.
 * @property {string} city - Company city.
 * @property {?number} stateId - Company state id.
 * @property {string} stateName - Company state name.
 * @property {?number} countryId - Company country id.
 * @property {string} countryName - Company country name.
 */

/**
 * @typedef {Object} Collaterate.Site~settingsUrls
 * @property {string} domainName - Custom domain name for the site.
 * @property {string} subdomainPrefix - Storefront subdomain prefix.
 * @property {string} homeUrl - Fully qualified home URL.
 */

/**
 * @typedef {Object} Collaterate.Site~settingsFeatures
 * @property {boolean} loyaltyEnabled - Whether the loyalty program is enabled.
 * @property {boolean} dataProfilesEnabled - Whether data profiles (personalization) are enabled.
 * @property {boolean} designToolEnabled - Whether the online design tool is enabled.
 * @property {boolean} multipleProjectsEnabled - Whether multiple projects (carts) are enabled.
 * @property {boolean} backOrderEnabled - Whether backorders are enabled.
 * @property {boolean} subscriptionDigitalDownloadEnabled - Whether subscription digital downloads are enabled.
 * @property {boolean} storefrontFlowV2 - Whether the v2 storefront flow is enabled.
 * @property {boolean} divisionOrderingEnabled - Whether ordering for divisions is enabled.
 */

/**
 * @typedef {Object} Collaterate.Site~settingsCommerce
 * @property {boolean} showPricing - Whether pricing is shown on the storefront.
 * @property {boolean} showDueDate - Whether due date is shown.
 * @property {boolean} isTaxExempt - Whether the site is tax exempt.
 * @property {boolean} orderApprovalEnabled - Whether order approval is enabled.
 * @property {boolean} allowStockQuantityChangeFromCart - Whether stock item quantity can change from the cart.
 * @property {boolean} allowPodQuantityChangeFromCart - Whether POD item quantity can change from the cart.
 * @property {boolean} allowCheckoutPaymentTypeOverride - Whether the checkout payment type can be overridden.
 * @property {boolean} allowUserAssignedCustomQuote - Whether signed-in users may request custom quotes.
 * @property {boolean} allowAnonymousCustomQuote - Whether anonymous users may request custom quotes.
 * @property {boolean} addToCartRedirectsToHome - Whether adding to cart redirects to home.
 * @property {?number} defaultShipCountryId - Default ship-to country id.
 * @property {string} defaultShipCountryName - Default ship-to country name.
 */

/**
 * @typedef {Object} Collaterate.Site~settingsAccess
 * @property {boolean} isPrivateSite - Whether the site is private (registration not open).
 * @property {?number} sessionTimeoutMinutes - Session timeout in minutes.
 */

/**
 * @typedef {Object} Collaterate.Site~settingsLoyalty
 * @property {boolean} isEnabled - Whether loyalty is enabled.
 * @property {string} publicApiKey - Yotpo public app key used by client-side loyalty widgets.
 * @property {string} pointsLabel - Display label for loyalty points.
 */

/**
 * @typedef {Object} Collaterate.Site~settingsSystem
 * @property {?number} uploadMaxSizeBytes - Maximum upload size in bytes.
 * @property {string} cacheBusting - Cache-busting token for static assets.
 * @property {boolean} clientDebug - Whether client debug mode is enabled.
 * @property {boolean} showTestingWatermark - Whether the testing watermark should be shown.
 */
